import React, { useEffect, useRef, useState } from 'react';
import type { PropsWithChildren } from 'react';

import { Box, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import dynamic from 'next/dynamic';

import type { MegaNavItem, MenuLink } from '../types';
import { DashboardNavItem } from './DashboardNavItem';

const MegaNavFlyout = dynamic(() => import('./MegaNavFlyout'));

const NAV_LINK_CLASSES =
  'inline-flex h-11 items-center justify-center rounded-xl px-4 font-medium text-od-content-primary no-underline outline-none transition hover:bg-[rgb(88_64_50_/_0.08)] active:bg-[rgb(88_64_50_/_0.16)] focus-visible:outline focus-visible:outline-1 focus-visible:outline-solid focus-visible:outline-offset-4 focus-visible:outline-[#81a2f2]';

interface MegaNavProps {
  slug: string;
  mainNavLinks: MenuLink[];
  megaNavData?: MegaNavItem[];
}

export const MegaNav: React.FC<MegaNavProps> = ({ slug, megaNavData }) => {
  const [openNavItem, setOpenNavItem] = useState<string | null>(null);
  const [flyoutTopOffset, setFlyoutTopOffset] = useState(72);
  const navRef = useRef<HTMLDivElement | null>(null);
  const closeTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = (itemName: string) => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    // Calculate the flyout top position based on where the nav actually is in the viewport
    // This accounts for any banners or other elements above the header
    if (navRef.current) {
      const rect = navRef.current.getBoundingClientRect();
      setFlyoutTopOffset(rect.bottom);
    }
    setOpenNavItem(itemName);
  };

  const handleMouseLeave = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = setTimeout(() => {
      setOpenNavItem(null);
      closeTimerRef.current = null;
    }, 400);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setOpenNavItem(null);
      }
    };

    if (openNavItem) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openNavItem]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  return (
    <Box ref={navRef} className="relative block justify-self-end [grid-column:2/3] [grid-row:1/2]">
      <Row
        role="list"
        analyticsName="cosmos-mega-nav-menu-list"
        className="m-0 hidden list-none justify-end gap-0 min-[1024px]:flex"
      >
        {megaNavData?.map((navItem) => {
          const isOpen = openNavItem === navItem.name;
          const hasSections = navItem.sections && navItem.sections.length > 0;

          return (
            <Box
              role="listitem"
              key={navItem.name}
              className="relative block"
              {...(hasSections && {
                onMouseEnter: () => handleMouseEnter(navItem.name),
                onMouseLeave: handleMouseLeave,
              })}
            >
              {hasSections ? (
                <>
                  <MegaNavLink
                    active={slug === navItem.href}
                    href={navItem.href}
                    name={navItem.name}
                    isOpen={isOpen}
                  >
                    <Text
                      as="span"
                      className="text-[16px] font-medium tracking-[-0.8px] text-inherit"
                    >
                      {navItem.name}
                    </Text>
                  </MegaNavLink>
                  <MegaNavFlyout
                    item={navItem}
                    isVisible={isOpen}
                    onMouseEnter={() => handleMouseEnter(navItem.name)}
                    onMouseLeave={handleMouseLeave}
                    topOffset={flyoutTopOffset}
                  />
                </>
              ) : (
                <Box
                  href={navItem.href}
                  aria-label={`View ${navItem.name}`}
                  aria-current={slug === navItem.href ? 'page' : undefined}
                  analyticsName={`cosmos-landing-page-v2-nav-link-${navItem.name}`}
                  className={NAV_LINK_CLASSES}
                >
                  <Text
                    as="span"
                    className="text-[16px] font-medium tracking-[-0.8px] text-inherit"
                  >
                    {navItem.name}
                  </Text>
                </Box>
              )}
            </Box>
          );
        })}
        <DashboardNavItem slug={slug} />
      </Row>
    </Box>
  );
};

const MegaNavLink: React.FC<
  PropsWithChildren<{
    href: string;
    active?: boolean;
    name: string;
    isOpen: boolean;
  }>
> = ({ children, name, active, isOpen, href }) => (
  <Box
    href={href}
    aria-label={`${name} menu`}
    aria-current={active ? 'page' : undefined}
    aria-expanded={isOpen}
    analyticsName={`cosmos-mega-nav-v2-nav-link-${name}`}
    className={cn(
      NAV_LINK_CLASSES,
      'cursor-pointer flex-row [text-rendering:auto]',
      isOpen && 'bg-[rgb(88_64_50_/_0.16)] hover:bg-[rgb(88_64_50_/_0.16)]',
    )}
  >
    {children}
    <ChevronDown isOpen={isOpen} />
  </Box>
);

const ChevronDown: React.FC<{ isOpen: boolean }> = ({ isOpen }) => (
  <svg
    aria-hidden="true"
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    className={cn('[margin-left:4px] transition-transform', isOpen && 'rotate-180')}
  >
    <path
      d="M3 5l3 3 3-3"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
